import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip as MuiTooltip } from '@mui/material';
import PropTypes from 'prop-types';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

const DATE_FORMAT = 'YYYY-MM-DD';
const TICK_FORMAT = 'MMM D';

const buildChartData = (rows, startDate, endDate) => {
    if (!startDate || !endDate) {
        return [];
    }

    const countsByDate = rows.reduce((acc, row) => ({
        ...acc,
        [row.local_date]: row.case_count,
    }), {});

    const data = [];
    const currentDate = startDate.clone().startOf('day');
    const lastDate = endDate.clone().startOf('day');

    while (!currentDate.isAfter(lastDate, 'day')) {
        const localDate = currentDate.format(DATE_FORMAT);
        data.push({
            caseCount: countsByDate[localDate] || 0,
            displayDate: currentDate.format(TICK_FORMAT),
            localDate,
        });
        currentDate.add(1, 'day');
    }

    return data;
};

function TotalScanVolumeChart({ analytics, startDate, endDate }) {
    const rows = analytics?.rows || [];
    const chartData = buildChartData(rows, startDate, endDate);
    const hasRows = rows.length > 0;

    return (
        <section className="dashboard-chart-card">
            <div className="dashboard-chart-header dashboard-chart-header--with-icon">
                <h2>Total Scan Volume Over Time</h2>
                <MuiTooltip title="Daily scan volume for the selected date range.">
                    <InfoOutlinedIcon className="dashboard-chart-info-icon" fontSize="small" />
                </MuiTooltip>
            </div>
            <div className="dashboard-chart-body">
                {hasRows ? (
                    <ResponsiveContainer width="100%" height={320}>
                        <AreaChart
                            data={chartData}
                            margin={{
                                top: 12,
                                right: 16,
                                left: -12,
                                bottom: 0,
                            }}
                        >
                            <defs>
                                <linearGradient id="scanVolumeFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#04c2ff" stopOpacity={0.28} />
                                    <stop offset="95%" stopColor="#04c2ff" stopOpacity={0.04} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#d9d9d9" strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="displayDate"
                                minTickGap={24}
                                stroke="#666666"
                                tickLine={false}
                                axisLine={false}
                            />
                            <YAxis
                                allowDecimals={false}
                                stroke="#666666"
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    border: '1px solid #000000',
                                    borderRadius: '6px',
                                }}
                                formatter={(value) => [value, 'Scan Volume']}
                                labelFormatter={(_, payload) => payload?.[0]?.payload?.localDate || ''}
                            />
                            <Area
                                type="linear"
                                dataKey="caseCount"
                                stroke="#04c2ff"
                                strokeWidth={2}
                                fill="url(#scanVolumeFill)"
                                activeDot={{ r: 5 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="dashboard-chart-empty">
                        <p>No data for the selected date range.</p>
                        <p>Try expanding the date range or adjusting filters.</p>
                    </div>
                )}
            </div>
        </section>
    );
}

TotalScanVolumeChart.propTypes = {
    analytics: PropTypes.shape({
        rows: PropTypes.arrayOf(PropTypes.shape({
            case_count: PropTypes.number,
            local_date: PropTypes.string,
        })),
    }),
    endDate: PropTypes.shape({}).isRequired,
    startDate: PropTypes.shape({}).isRequired,
};

TotalScanVolumeChart.defaultProps = {
    analytics: null,
};

export default TotalScanVolumeChart;
